/**
 * Whether a right-click is ours to answer, or the browser's.
 *
 * **The app's custom menu is the exception, not the rule.** Alex's call (日本-318): the browser's
 * own context menu is suppressed only on elements that actually have a menu, because Copy on a
 * line of Japanese is a real thing to do while reading, and a right-click that silently loses it
 * is worse than one that opens nothing.
 *
 * Duck-typed and DOM-free for `lib/dom.ts`'s reason, which applies with more force here: this is
 * the guard deciding whether the reader can copy the sentence in front of them, and it should not
 * go untested because a `MouseEvent` and a `Selection` were awkward to construct.
 *
 * The caller resolves the one genuinely DOM-shaped question — is there a live selection inside
 * this element — and hands the answer in as a boolean. Same trick, same reason.
 */

import { isTextEntry } from "./dom";

/**
 * True when the app should open its own menu and call `preventDefault`.
 *
 * Three ways to say no, and they are not interchangeable:
 *
 * - **A text entry.** Cut, Paste, Undo and the spellchecker live in the browser's menu and this
 *   app reimplements none of them. `isTextEntry` is shared with Read's and Cards' keyboard guards,
 *   so a field that is safe from `j`/`k` is safe from this too, by construction.
 * - **Shift held.** Firefox forces its own menu on Shift+right-click regardless of what a page
 *   asks for, so honouring it costs nothing and buys a consistent escape hatch everywhere else.
 *   That makes the browser menu reachable on *any* element, which is what makes suppressing it
 *   elsewhere defensible.
 * - **A selection inside the target.** The reason the whole rule exists. Selecting a phrase and
 *   right-clicking it must offer Copy, Look up and Search, which is most of why anyone
 *   right-clicks text at all.
 */
export function shouldOpenContextMenu(
  event: { target?: EventTarget | null; shiftKey?: boolean },
  context: { selectionInTarget: boolean },
): boolean {
  if (event.shiftKey === true) return false;
  if (isTextEntry(event.target)) return false;
  if (context.selectionInTarget) return false;
  return true;
}

/**
 * Is there a live selection inside this element?
 *
 * The impure half, deliberately kept to one line so the rule above stays testable. A collapsed
 * selection is a caret rather than a selection and does not count — otherwise every click that
 * placed a caret would hand the menu back to the browser for ever after.
 */
export function hasSelectionIn(element: Node): boolean {
  const selection = typeof window === "undefined" ? null : window.getSelection();
  return (
    selection !== null &&
    !selection.isCollapsed &&
    selection.anchorNode !== null &&
    element.contains(selection.anchorNode)
  );
}
